import type {
	CreateFeatureRequestMutation,
	FeatureRequestObject,
} from '@feature-request/app-graphql';
import { ShieldAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMinLoading } from '@/hooks/use-min-loading';
import { Spinner } from '@/ui/components/ui/spinner';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import { FeatureForm } from '../components/feature-form';
import { FeatureList } from '../components/feature-list';
import { FeatureToolbar } from '../components/feature-toolbar';
import { useAnonymousId } from '../hooks/use-anonymous-id';
import { useEmbedVerification } from '../hooks/use-embed-verification';
import { useFeatureAnimations } from '../hooks/use-feature-animations';
import {
	PROJECT_ID,
	toFeature,
	useCreateFeatureRequest,
	useFeatureRequests,
	useRemoveUpvoteFeatureRequest,
	useUpvoteFeatureRequest,
} from '../hooks/use-feature-requests';
import { type SortBy, sortFeatures } from '../utils/feature.utils';

/** Best-effort parent-page origin from document.referrer (empty in same-origin). */
function getEmbedOrigin(): string {
	try {
		const ref = document.referrer;
		if (!ref) return '';
		return new URL(ref).origin;
	} catch {
		return '';
	}
}

export function UserFeedbackPage() {
	const [params] = useSearchParams();

	const paramProjectId = params.get('projectId') ?? '';
	const pageHmac = params.get('pageHmac') ?? '';
	const timestamp = params.get('timestamp') ?? '';
	const userId = params.get('userId') ?? '';
	const userHmac = params.get('userHmac') ?? '';

	const isEmbed = Boolean(pageHmac);
	const projectId = paramProjectId || PROJECT_ID;
	const embedOrigin = isEmbed ? getEmbedOrigin() : '';

	const { verification, verifying } = useEmbedVerification({
		isEmbed,
		projectId,
		pageHmac,
		timestamp,
		embedOrigin,
	});

	const votingMode = (verification?.votingMode ?? 'open') as
		| 'open'
		| 'cookie'
		| 'tracked';

	const { queryId: anonymousId, getOrCreate: getOrCreateAnonymousId } =
		useAnonymousId(projectId, votingMode);

	const queryExtRefId =
		votingMode === 'tracked' ? userId || null : anonymousId;

	const {
		data,
		loading: rawLoading,
		error,
	} = useFeatureRequests(
		isEmbed ? (verification ? projectId : '') : projectId,
		queryExtRefId,
	);
	const [createFeatureRequest] = useCreateFeatureRequest(
		projectId,
		queryExtRefId,
	);
	const [upvoteFeatureRequest] = useUpvoteFeatureRequest();
	const [removeUpvoteFeatureRequest] = useRemoveUpvoteFeatureRequest();

	const loading = useMinLoading(rawLoading);

	const [poppedId, setPoppedId] = useState<string | null>(null);
	const [newId, setNewId] = useState<string | null>(null);
	const [sortBy, setSortBy] = useState<SortBy>('votes');
	const [formOpen, setFormOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [flaggedWarning, setFlaggedWarning] = useState(false);
	const [formKey, setFormKey] = useState(0);

	const listRef = useRef<Map<string, HTMLDivElement>>(new Map());
	const {
		capturePositions,
		animateEntrance,
		animateMove,
		animateSort,
		animateShift,
	} = useFeatureAnimations(listRef);

	const handleAddFeature = (title: string, description: string) => {
		const pos = capturePositions();
		const creatorId =
			votingMode === 'tracked'
				? userId || undefined
				: (getOrCreateAnonymousId() ?? undefined);
		const hmac =
			votingMode === 'tracked' ? userHmac || undefined : undefined;
		setSubmitting(true);
		setFlaggedWarning(false);
		createFeatureRequest({
			variables: {
				input: {
					projectId,
					title,
					description: description || null,
					...(creatorId && { createdByExtRefId: creatorId }),
					...(hmac && { extRefHmac: hmac }),
				},
			},
			onCompleted(mutData: CreateFeatureRequestMutation) {
				setSubmitting(false);
				if (mutData.createFeatureRequest.isFlaggedByModeration) {
					setFlaggedWarning(true);
					return;
				}
				const thisId = mutData.createFeatureRequest.id;
				setFlaggedWarning(false);
				setFormOpen(false);
				setFormKey((k) => k + 1);
				setSortBy('newest');
				setNewId(thisId);
				setTimeout(() => {
					animateShift(pos, thisId);
					animateEntrance(thisId);
					setTimeout(() => setNewId(null), 700);
				}, 0);
			},
			onError() {
				setSubmitting(false);
			},
		});
	};

	const handleVote = (id: string) => {
		const current = (
			data?.featureRequests ?? ([] as FeatureRequestObject[])
		).find((f: FeatureRequestObject) => f.id === id);
		if (!current) return;

		const pos = capturePositions();
		setPoppedId(id);
		setTimeout(() => setPoppedId(null), 500);

		const voterId =
			votingMode === 'tracked'
				? userId || undefined
				: (getOrCreateAnonymousId() ?? undefined);
		const hmac =
			votingMode === 'tracked' ? userHmac || undefined : undefined;

		const voteInput = {
			featureRequestId: id,
			...(voterId && { extRefId: voterId }),
			...(hmac && { extRefHmac: hmac }),
		};

		const voted = current.isVoted;

		if (voted) {
			removeUpvoteFeatureRequest({
				variables: { input: voteInput },
				optimisticResponse: {
					removeUpvoteFeatureRequest: {
						__typename: 'FeatureRequestObject',
						id,
						upvoteCount: Math.max(
							0,
							(current.upvoteCount ?? 0) - 1,
						),
						isVoted: false,
						updatedAt: new Date().toISOString(),
					},
				},
			});
			setTimeout(() => animateMove(pos, id, 'down'), 0);
		} else {
			upvoteFeatureRequest({
				variables: { input: voteInput },
				optimisticResponse: {
					upvoteFeatureRequest: {
						__typename: 'FeatureRequestObject',
						id,
						upvoteCount: (current.upvoteCount ?? 0) + 1,
						isVoted: true,
						updatedAt: new Date().toISOString(),
					},
				},
			});
			setTimeout(() => animateMove(pos, id, 'up'), 0);
		}
	};

	const handleSortChange = (newSort: SortBy) => {
		if (newSort === sortBy) return;
		const pos = capturePositions();
		setSortBy(newSort);
		setTimeout(() => animateSort(pos), 0);
	};

	// ── Embed-mode guard states ───────────────────────────────────────────────
	if (isEmbed && verifying) {
		return (
			<div className='min-h-screen bg-background flex items-center justify-center'>
				<Spinner size='lg' />
			</div>
		);
	}

	if (isEmbed && !verification) {
		return (
			<div className='min-h-screen bg-background flex items-center justify-center px-6'>
				<StatusPlaceholder
					icon={ShieldAlert}
					title='Widget verification failed'
					description='Check your HMAC token, timestamp, and allowed origins.'
					variant='error'
				/>
			</div>
		);
	}

	const features = sortFeatures(
		(data?.featureRequests ?? []).map((f) =>
			toFeature(f as FeatureRequestObject),
		),
		sortBy,
	);

	return (
		<div className='min-h-screen bg-background px-8 py-8'>
			<div className='max-w-xl mx-auto'>
				<div className='mb-8'>
					<div className='flex items-start gap-1.5'>
						<span className='text-xl font-light text-foreground/70 tracking-[-0.01em]'>
							Feature requests
						</span>
						<span className='text-[10px] font-semibold tracking-[0.06em] uppercase text-accent bg-secondary px-1.5 py-0.5 rounded'>
							Beta
						</span>
					</div>
				</div>

				<FeatureToolbar
					sortBy={sortBy}
					formOpen={formOpen}
					onSortChange={handleSortChange}
					onToggleForm={() => {
						setFormOpen((f) => !f);
						setFlaggedWarning(false);
					}}
				/>

				<FeatureForm
					key={formKey}
					isOpen={formOpen}
					submitting={submitting}
					error={
						flaggedWarning
							? 'Please remove inappropriate language.'
							: null
					}
					onSubmit={handleAddFeature}
					onClose={() => setFormOpen(false)}
				/>

				<FeatureList
					loading={loading}
					error={Boolean(error)}
					features={features}
					poppedId={poppedId}
					newId={newId}
					onVote={handleVote}
					onRef={(id, el) => {
						if (el) listRef.current.set(id, el);
						else listRef.current.delete(id);
					}}
				/>
			</div>
		</div>
	);
}
